import { Link } from "react-router";
import { Icon } from "@/components/icons";
import { RowActionsMenu, type RowAction } from "@/components/row-actions-menu";
import { Tooltip } from "@/components/tooltip";
import type { Batch } from "../../_data/batches";
import { formatDate } from "../../_lib/format";
import { BatchStatusIcon } from "./batch-status-icon";

type BatchHistoryListProps = {
  batches: Batch[];
  onRemove: (id: string) => void;
  onDownload: (id: string) => void;
  onClearAll: () => void;
};

/**
 * Recent-batches list. The caller (BatchEmptyState) guarantees
 * `batches.length > 0` — the loading / error / empty states live there.
 */
export function BatchHistoryList({
  batches,
  onRemove,
  onDownload,
  onClearAll,
}: BatchHistoryListProps) {
  return (
    <section className="flex w-full flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-body font-medium text-black">Recent batches</h3>
        <button
          type="button"
          onClick={onClearAll}
          className="flex items-center gap-1.5 rounded-[4px] text-body font-medium text-black transition-colors hover:text-grey-900"
        >
          <Icon name="delete" size={20} />
          Clear all
        </button>
      </div>
      <div className="flex flex-col overflow-hidden rounded-[4px] border border-grey-200 bg-white shadow-flat">
        {batches.map((batch, i) => (
          <BatchRow
            key={batch.id}
            batch={batch}
            isLast={i === batches.length - 1}
            onRemove={onRemove}
            onDownload={onDownload}
          />
        ))}
      </div>
    </section>
  );
}

function BatchRow({
  batch,
  isLast,
  onRemove,
  onDownload,
}: {
  batch: Batch;
  isLast: boolean;
  onRemove: (id: string) => void;
  onDownload: (id: string) => void;
}) {
  // The live LIST payload carries `nameCount` with empty `rows` (rows exist only
  // at detail level). The "with matches" figure is detail-only, so show it only
  // when rows are present (e.g. fixtures) and omit it for list-level entries.
  const total = batch.nameCount ?? batch.rows.length;
  const withMatches = batch.rows.length > 0 ? batch.rows.filter((r) => r.hits > 0).length : null;

  return (
    <div
      className={[
        "group relative flex items-center transition-colors hover:bg-grey-50",
        isLast ? "" : "border-b border-grey-100",
      ].join(" ")}
    >
      <Tooltip
        title={batch.parsingErrorMessages?.length ? "Parsing errors" : undefined}
        description={
          batch.parsingErrorMessages?.length ? batch.parsingErrorMessages.join("\n") : undefined
        }
        wrapperClassName="min-w-0 flex-1"
      >
        <Link
          to={`/batch/${batch.id}`}
          className="flex min-w-0 flex-1 items-center gap-4 py-2 pl-4 pr-2"
        >
          <div className="flex shrink-0 items-center gap-1.5">
            <BatchStatusIcon
              processing={batch.processing}
              parsingErrorMessages={batch.parsingErrorMessages}
            />
            <img src="/brand/File.svg" alt="" aria-hidden className="h-9 w-auto" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="truncate text-small font-medium text-black">{batch.filename}</p>
            <p className="truncate text-small text-grey-600">
              {formatDate(batch.uploadedAt)} · {total} {total === 1 ? "name" : "names"}
              {withMatches !== null ? ` · ${withMatches} with matches` : ""}
            </p>
          </div>
        </Link>
      </Tooltip>
      <div className="mr-2 shrink-0">
        <RowActionsMenu
          actions={rowActions(batch, onDownload, onRemove)}
          label={`Actions for ${batch.filename}`}
        />
      </div>
    </div>
  );
}

/** Download is omitted, and Delete is disabled, for a still-processing batch —
 *  there's no results file yet, and deleting mid-screen would race the backend job. */
function rowActions(
  batch: Batch,
  onDownload: (id: string) => void,
  onRemove: (id: string) => void,
): RowAction[] {
  const actions: RowAction[] = [];
  if (!batch.processing) {
    actions.push({
      label: "Download results",
      icon: "download",
      onClick: () => onDownload(batch.id),
    });
  }
  actions.push({
    label: "Delete batch",
    icon: "delete",
    onClick: () => onRemove(batch.id),
    tone: "danger",
    disabled: batch.processing,
    disabledReason: batch.processing ? "Wait for processing to finish before deleting" : undefined,
  });
  return actions;
}
